import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Key, Plus, Prohibit, Trash, UserPlus, ArrowCounterClockwise } from "@phosphor-icons/react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { setupLink, useUser } from "@/lib/user";
import { btnGhost, btnPrimary, inputCls, labelCls } from "@/lib/ui";
import { fmtDate, fmtHours, money } from "@/components/admin/format";

const STATUS = {
  active: "border-[#10B981]/40 text-[#10B981]",
  invited: "border-[#F59E0B]/40 text-[#F59E0B]",
  disabled: "border-[var(--o-border-strong)] text-[var(--o-muted3)]",
};

// Shown once after adding someone or making a reset link: there is no email service, so the admin sends it.
const LinkBox = ({ link, onClose }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy; select the link and copy it");
    }
  };
  return (
    <div className="border border-[#F59E0B]/50 bg-[#F59E0B]/5 p-4 space-y-3" data-testid="setup-link-box">
      <div className="text-sm">
        <span className="font-semibold">{link.purpose === "reset" ? "Password reset link" : "Setup link"} for {link.name}</span>
        <span className="text-[var(--o-muted2)]"> · send it to {link.email} yourself. It works once and expires in {link.hours} hours. It won't be shown again.</span>
      </div>
      <div className="flex gap-2">
        <input readOnly value={link.url} onFocus={(e) => e.target.select()} className={`${inputCls} font-mono text-xs`} data-testid="setup-link" />
        <button onClick={copy} className={btnGhost} data-testid="setup-link-copy"><Copy size={14} /> Copy</button>
        <button onClick={onClose} className={btnGhost}>Done</button>
      </div>
    </div>
  );
};

const AddUser = ({ onAdded }) => {
  const [f, setF] = useState({ name: "", email: "", role: "member" });
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", f);
      setF({ name: "", email: "", role: "member" });
      onAdded(data, "invite");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={add} className="grid sm:grid-cols-[1fr_1.3fr_140px_auto] gap-3 items-end p-5 border-b border-[var(--o-border)]" data-testid="add-user-form">
      <label className="space-y-1.5 block"><span className={labelCls}>Name</span>
        <input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoComplete="off" data-testid="add-user-name" /></label>
      <label className="space-y-1.5 block"><span className={labelCls}>Email</span>
        <input type="email" className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} autoComplete="off" data-testid="add-user-email" /></label>
      <label className="space-y-1.5 block"><span className={labelCls}>Role</span>
        <select className={inputCls} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="add-user-role">
          <option value="member">Member</option><option value="admin">Admin</option>
        </select></label>
      <button className={btnPrimary} disabled={busy || !f.name.trim() || !f.email.trim()} data-testid="add-user-submit"><UserPlus size={15} /> Add</button>
    </form>
  );
};

export const UsersPanel = () => {
  const me = useUser();
  const [rows, setRows] = useState(null);
  const [link, setLink] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => api.get("/admin/users").then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const showLink = (data, purpose) => {
    setLink({ url: setupLink(data.token), name: data.user.name, email: data.user.email, hours: data.expires_hours, purpose });
    load();
  };
  const patch = async (u, body, done) => {
    try {
      await api.patch(`/admin/users/${u.id}`, body);
      toast.success(done);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const reset = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset`);
      showLink(data, u.status === "invited" ? "invite" : "reset");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (u) => {
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.name} removed${data.meetings_moved ? `; ${data.meetings_moved} meeting${data.meetings_moved === 1 ? "" : "s"} moved to you` : ""}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="users-panel">
      {link && <LinkBox link={link} onClose={() => setLink(null)} />}
      <div className="border border-[var(--o-border)]">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-[var(--o-border)]">
          <div>
            <h2 className="font-display text-base md:text-lg font-bold">People</h2>
            <p className="text-xs text-[var(--o-muted2)] mt-1">Everyone added here signs in at /login with their own email and password and sees only their own meetings. Give someone the admin role if they should reach this area with their own sign-in; the admin password always can.</p>
          </div>
          <button onClick={() => setAdding((v) => !v)} className={btnGhost} data-testid="add-user-toggle"><Plus size={14} /> Add person</button>
        </div>
        {adding && <AddUser onAdded={(d) => { setAdding(false); showLink(d, "invite"); }} />}
        {!rows && <div className="h-24 shimmer m-5" />}
        {rows?.map((u) => {
          const self = u.id === me.id;
          return (
            <div key={u.id} className="grid grid-cols-1 lg:grid-cols-[1.4fr_110px_90px_1.2fr_auto] gap-x-4 gap-y-2 items-center px-5 py-3.5 border-b border-[var(--o-border-soft)] last:border-b-0" data-testid={`user-row-${u.email}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{u.name}{self && <span className="font-mono text-[10px] text-[var(--o-muted3)]"> · you</span>}</div>
                <div className="font-mono text-[11px] text-[var(--o-muted2)] truncate">{u.email}</div>
              </div>
              <select value={u.role} disabled={self} onChange={(e) => patch(u, { role: e.target.value }, `${u.name} is now ${e.target.value === "admin" ? "an admin" : "a member"}`)} className="h-8 bg-[var(--o-bg)] border border-[var(--o-border)] px-2 font-mono text-xs disabled:opacity-50" aria-label={`Role for ${u.name}`} data-testid={`user-role-${u.email}`}>
                <option value="member">Member</option><option value="admin">Admin</option>
              </select>
              <span className={`w-fit font-mono text-[10px] uppercase px-1.5 py-0.5 border ${STATUS[u.status]}`} data-testid={`user-status-${u.email}`}>{u.status}</span>
              <div className="font-mono text-[11px] text-[var(--o-muted2)]">
                {u.usage.meetings} meeting{u.usage.meetings === 1 ? "" : "s"} · {fmtHours(u.usage.seconds)} · {money(u.usage.cost)}
                <div className="text-[var(--o-muted3)]">{u.last_login_at ? `signed in ${fmtDate(u.last_login_at)}` : u.status === "invited" ? "hasn't set a password yet" : "never signed in"}</div>
              </div>
              <div className="flex flex-wrap gap-1.5 lg:justify-end">
                <button onClick={() => reset(u)} disabled={u.status === "disabled"} className={`${btnGhost} !px-2.5 !py-1.5 text-xs`} title={u.status === "invited" ? "New setup link (the old one stops working)" : "Password reset link (signs them out everywhere)"} data-testid={`user-reset-${u.email}`}>
                  <Key size={13} /> {u.status === "invited" ? "New link" : "Reset"}
                </button>
                {u.status === "disabled" ? (
                  <button onClick={() => patch(u, { status: "active" }, `${u.name} can sign in again`)} className={`${btnGhost} !px-2.5 !py-1.5 text-xs`} data-testid={`user-enable-${u.email}`}><ArrowCounterClockwise size={13} /> Enable</button>
                ) : (
                  <button onClick={() => setConfirm({ kind: "disable", u })} disabled={self} className={`${btnGhost} !px-2.5 !py-1.5 text-xs`} data-testid={`user-disable-${u.email}`}><Prohibit size={13} /> Disable</button>
                )}
                <button onClick={() => setConfirm({ kind: "remove", u })} disabled={self} className={`${btnGhost} !px-2.5 !py-1.5 text-xs text-[#EF4444]`} data-testid={`user-remove-${u.email}`}><Trash size={13} /></button>
              </div>
            </div>
          );
        })}
      </div>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[var(--o-bg2)] border-[var(--o-border)] rounded-none" data-testid="user-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">{confirm?.kind === "remove" ? `Remove ${confirm?.u.name}?` : `Disable ${confirm?.u.name}?`}</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--o-muted)]">
              {confirm?.kind === "remove"
                ? "Their account is deleted. Their saved meetings and briefings move to you, so nothing is lost. This can't be undone."
                : "They are signed out now and can't sign in until you enable them again. Their meetings stay as they are."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[var(--o-border)] bg-transparent">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-none bg-[var(--o-fg)] text-[var(--o-bg)] hover:opacity-90" data-testid="user-confirm-yes"
              onClick={() => (confirm.kind === "remove" ? remove(confirm.u) : patch(confirm.u, { status: "disabled" }, `${confirm.u.name} is disabled`))}>
              {confirm?.kind === "remove" ? "Remove" : "Disable"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
